import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Award, CheckCircle2, ExternalLink, FileText, Headphones, Link2, ListChecks, Loader2, Lock, PlayCircle, Send, Type } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, API } from "@/lib/api";
import { badge, btnPrimary, card, Skeleton } from "@/components/portal/ui";
import LockedVideo, { youtubeId } from "@/components/portal/LockedVideo";

const ICONS = { video: PlayCircle, text: Type, pdf: FileText, audio: Headphones, link: Link2, quiz: ListChecks };

function VideoGate({ lesson, onReady }) {
  const [watch, setWatch] = useState({ watched: 0, duration: 0 });
  const lastSent = useRef(0);
  const ratio = watch.duration ? Math.min(1, watch.watched / watch.duration) : 0;
  const need = lesson.min_watch_ratio || 0.9;

  const report = useCallback((data) => {
    setWatch({ watched: data.watched, duration: data.duration });
    const now = Date.now();
    if (!data.duration) return;
    if (data.ended || now - lastSent.current > 8000) {
      lastSent.current = now;
      api.post(`${lesson._base}/watch`, { watched: data.watched, duration: data.duration })
        .then((r) => { if (r.data.can_complete) onReady?.(); })
        .catch(() => {});
    }
  }, [lesson._base, onReady]);

  return (
    <div data-testid="video-gate">
      <LockedVideo url={lesson._videoUrl} onProgress={report} />
      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2" data-testid="video-watch-progress">
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.round(ratio * 100)}%`, transition: "width .3s linear" }} /></div>
        <p className="mt-1.5 text-[11px] font-bold text-slate-500">
          {ratio >= need ? "Video selesai ditonton — silakan lanjut" : `Tonton ${Math.round(need * 100)}% video untuk membuka tombol lanjut · progres ${Math.round(ratio * 100)}%`}
        </p>
      </div>
    </div>
  );
}

function QuizPlayer({ lesson, onDone }) {
  const questions = lesson.quiz?.questions || [];
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const ready = answers.every((a) => a !== null);
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`${lesson._base}/quiz`, { answers });
      setResult(data);
      if (data.passed) {
        toast.success(`Lulus! Nilai ${data.score} — sesi Grow ikut bertambah`);
        onDone(data);
      } else {
        toast.error(`Nilai ${data.score}. Minimal ${data.passing_grade}. Coba lagi!`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="quiz-player">
      {questions.map((q, i) => (
        <div key={i}>
          <p className="text-sm font-bold text-slate-900">{i + 1}. {q.q}</p>
          <div className="mt-2 grid gap-2">
            {q.options.map((opt, j) => (
              <button key={j} type="button" data-testid={`quiz-q${i}-opt${j}`} onClick={() => setAnswers((a) => a.map((x, k) => (k === i ? j : x)))}
                className={`text-left text-sm rounded-xl border px-4 py-2.5 transition active:scale-[0.98] ${answers[i] === j ? "border-blue-600 bg-blue-50 font-bold text-blue-700" : "border-slate-200 hover:border-slate-300"}`}>{opt}</button>
            ))}
          </div>
        </div>
      ))}
      {result && !result.passed && <p className="text-xs font-bold text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="quiz-fail-note">Nilai {result.score}/100 · benar {result.correct}/{result.total}. Nilai kelulusan {result.passing_grade}.</p>}
      <button onClick={submit} disabled={!ready || loading} data-testid="quiz-submit-button" className={`${btnPrimary} w-full`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Kumpulkan Jawaban</>}
      </button>
    </div>
  );
}

function LessonBody({ lesson, onDone }) {
  const [saving, setSaving] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const complete = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`${lesson._base}/complete`);
      toast.success("Materi selesai");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const isYt = lesson.type === "video" && !!youtubeId(lesson.url);
  const isFile = lesson.type === "video" && /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(lesson.url || "");
  const isPlayable = isYt || isFile;
  const gateOpen = lesson.done || !isPlayable || videoReady;
  return (
    <div className="space-y-4" data-testid={`lesson-body-${lesson.type}`}>
      {lesson.type === "video" && (isPlayable
        ? <VideoGate lesson={{ ...lesson, _videoUrl: isFile ? fileUrl(lesson.url) : lesson.url }} onReady={() => setVideoReady(true)} />
        : <a href={lesson.url} target="_blank" rel="noreferrer" className={`${btnPrimary} w-full`}>Tonton Video <ExternalLink className="w-4 h-4" /></a>)}
      {lesson.content && <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{lesson.content}</p>}
      {lesson.type === "pdf" && <a href={fileUrl(lesson.url)} target="_blank" rel="noreferrer" data-testid="lesson-pdf-link" className="flex items-center gap-3 bg-slate-50 rounded-2xl p-4 font-bold text-sm text-slate-800 hover:bg-slate-100 transition"><FileText className="w-5 h-5 text-red-500" /> Buka dokumen PDF <ExternalLink className="w-4 h-4 ml-auto text-slate-400" /></a>}
      {lesson.type === "audio" && <audio controls src={fileUrl(lesson.url)} className="w-full" data-testid="lesson-audio" />}
      {lesson.type === "link" && <a href={lesson.url} target="_blank" rel="noreferrer" data-testid="lesson-link" className="flex items-center gap-3 bg-blue-50 rounded-2xl p-4 font-bold text-sm text-blue-700 hover:bg-blue-100 transition"><Link2 className="w-5 h-5" /> {lesson.url} <ExternalLink className="w-4 h-4 ml-auto" /></a>}
      {lesson.task && <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4" data-testid="lesson-task"><p className="text-[11px] font-black uppercase tracking-wider text-amber-700">Tugas</p><p className="mt-1 text-sm text-amber-900 whitespace-pre-line">{lesson.task}</p></div>}
      {lesson.type === "quiz" ? <QuizPlayer lesson={lesson} onDone={onDone} /> : (
        <button onClick={complete} disabled={saving || lesson.done || !gateOpen} data-testid="lesson-complete-button" className={`${btnPrimary} w-full ${lesson.done ? "!bg-emerald-500" : ""}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : lesson.done ? <><CheckCircle2 className="w-4 h-4" /> Sudah Selesai</> : !gateOpen ? <><Lock className="w-4 h-4" /> Tonton video sampai selesai</> : <>{lesson.task ? "Tugas Selesai & Lanjut" : "Tandai Selesai"}</>}
        </button>
      )}
    </div>
  );
}

function CertificateCard({ course }) {
  const url = `${window.location.origin}/verify/${course.progress.cert_number}`;
  return (
    <div className={`${card} text-center !bg-slate-900 !border-slate-800 text-white`} data-testid="certificate-card">
      <span className="w-14 h-14 mx-auto rounded-2xl bg-amber-400/15 text-amber-300 grid place-items-center"><Award className="w-7 h-7" /></span>
      <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.2em] text-blue-300">Sertifikat Kelulusan</p>
      <p className="mt-1 text-xl font-black">{course.title}</p>
      <p className="mt-1 text-xs text-white/50" data-testid="certificate-number">No. {course.progress.cert_number}</p>
      <div className="mt-4 inline-block bg-white p-3 rounded-2xl"><QRCodeSVG value={url} size={110} data-testid="certificate-qr" /></div>
      <p className="mt-3 text-[11px] text-white/40">Pindai QR untuk verifikasi keaslian sertifikat</p>
      <a href={`${API}/portal/lms/certificates/${encodeURIComponent(course.progress.cert_number)}/image`}
        target="_blank" rel="noopener noreferrer" data-testid="certificate-download"
        className="mt-4 inline-flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-bold rounded-full px-6 py-2.5 hover:bg-amber-300 active:scale-95 transition">
        <Award className="w-4 h-4" /> Unduh Sertifikat
      </a>
    </div>
  );
}

export default function CourseView() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const load = () => api.get(`/portal/lms/courses/${courseId}`).then((r) => {
    const done = new Set(r.data.progress.done || []);
    const lessons = r.data.lessons.map((l) => ({ ...l, done: done.has(l.id), _base: `/portal/lms/courses/${courseId}/lessons/${l.id}` }));
    setCourse({ ...r.data, lessons });
    const nextOpen = lessons.find((l) => !l.done && !l.locked) || lessons[0];
    setActive((a) => (a && lessons.some((l) => l.id === a && !l.locked) ? a : nextOpen?.id ?? null));
  }).catch((e) => setError(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCourse(null); setActive(null); load(); }, [courseId]);

  const enroll = async () => {
    setEnrolling(true);
    try {
      await api.post(`/portal/lms/courses/${courseId}/enroll`);
      toast.success("Pendaftaran dikirim. Menunggu persetujuan admin.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setEnrolling(false);
    }
  };

  const lesson = useMemo(() => course?.lessons.find((l) => l.id === active), [course, active]);
  if (error) return <div className="p-8 text-center" data-testid="course-error"><p className="text-sm text-slate-500">{error}</p><Link to="/portal/elearning" className="text-blue-600 font-bold text-sm">← Kembali</Link></div>;
  if (!course) return <div className="p-5 pt-14"><Skeleton className="h-96" /></div>;

  const enrolled = course.enrollment === "approved";
  return (
    <div data-testid="portal-course-view">
      <div className="px-5 pt-10 flex items-center gap-3">
        <Link to="/portal/elearning" data-testid="course-back-link" className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">{course.category || "Course"}</p><h1 className="font-extrabold text-slate-900 truncate">{course.title}</h1></div>
      </div>

      {!enrolled ? (
        <div className="px-5 mt-6"><div className={`${card} text-center`} data-testid="course-enroll-gate">
          <span className="w-14 h-14 mx-auto rounded-2xl bg-blue-100 text-blue-700 grid place-items-center"><ListChecks className="w-7 h-7" /></span>
          <p className="mt-3 font-extrabold text-slate-900">{course.enrollment === "pending" ? "Pendaftaran Diproses" : "Ikuti Course Ini"}</p>
          <p className="mt-1 text-sm text-slate-500">{course.enrollment === "pending" ? "Admin sedang meninjau pendaftaranmu." : `${course.lessons.length} materi · Instruktur ${course.instructor || "Tim GIM"}. Pendaftaran memerlukan persetujuan admin.`}</p>
          {course.enrollment !== "pending" && <button onClick={enroll} disabled={enrolling} data-testid="course-enroll-button" className={`${btnPrimary} w-full mt-5`}>{enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Daftar Course"}</button>}
        </div></div>
      ) : (
        <>
          <div className="px-5 mt-5">
            <div className="flex items-center justify-between text-xs font-bold text-slate-500"><span>Progress</span><span data-testid="course-progress-label">{course.progress.percent}%</span></div>
            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${course.progress.percent}%`, transition: "width .8s cubic-bezier(.22,1,.36,1)" }} /></div>
          </div>
          <div className="px-5 mt-5 space-y-3">
            {course.locked_reason && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="course-locked-banner">
                <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm font-bold text-amber-800">{course.locked_reason}</p>
              </div>
            )}
            {course.lessons.map((l, i) => {
              const Icon = ICONS[l.type] || Type;
              const open = active === l.id && !l.locked;
              return (
                <div key={l.id} className={`${card} !p-0 overflow-hidden ${l.locked ? "opacity-70" : ""}`} data-testid={`lesson-${l.id}`}>
                  <button onClick={() => (l.locked ? toast.error(l.lock_reason || "Materi terkunci") : setActive(open ? null : l.id))} data-testid={`lesson-toggle-${l.id}`} className="w-full flex items-center gap-3 p-4 text-left">
                    <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${l.done ? "bg-emerald-500 text-white" : l.locked ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500"}`}>{l.done ? <CheckCircle2 className="w-5 h-5" /> : l.locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}</span>
                    <span className="flex-1 min-w-0"><span className="block text-sm font-bold text-slate-900 truncate">{i + 1}. {l.title}</span><span className="block text-[11px] text-slate-400 capitalize">{l.locked ? (l.lock_reason || "Terkunci") : `${l.type}${l.type === "quiz" ? ` · lulus ≥ ${l.quiz?.passing_grade || course.passing_grade}` : ""}`}</span></span>
                    {l.type === "quiz" && course.progress.quiz_scores?.[l.id] != null && <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${badge[l.done ? "success" : "warn"]}`}>{course.progress.quiz_scores[l.id]}</span>}
                  </button>
                  {open && <div className="px-4 pb-5 pt-1 border-t border-slate-50"><LessonBody lesson={l} onDone={load} /></div>}
                </div>
              );
            })}
            {course.progress.completed && <CertificateCard course={course} />}
          </div>
        </>
      )}
    </div>
  );
}
